/* eslint-disable no-restricted-syntax */
import { InternalCategory } from '@gozoki/api-types';

/**
 * Base interface for items that can be categorized
 */
interface Categorizable {
    internalCategory: InternalCategory;
}

/**
 * Item with category flag for PDF generation
 */
export interface ItemWithCategory extends Record<string, unknown> {
    isNewCategory: boolean;
}

/**
 * Maps items by their internal categories
 * @param items - Array of items to categorize
 * @returns Object containing category map and list of categories
 */
export const mapEntriesByCategory = <T extends Categorizable>(
    items: T[]
): { map: { [key: number]: T[] }; categories: InternalCategory[] } => {
    const ICMap: { [key: number]: T[] } = {};
    const internalCategories: InternalCategory[] = [];

    items.forEach((item) => {
        if (ICMap[item.internalCategory.id]) {
            ICMap[item.internalCategory.id].push(item);
        } else {
            ICMap[item.internalCategory.id] = [item];
            internalCategories.push(item.internalCategory);
        }
    });

    return { map: ICMap, categories: internalCategories };
};

/**
 * Chunks categorized items into pages for PDF generation
 * @param entriesMap - Map of items by category ID
 * @param categories - List of categories
 * @param chunkSize - Maximum number of lines per page
 * @returns Array of item chunks, each chunk representing a page
 */
export const chunkEntriesByCategory = <T>(
    entriesMap: { [key: number]: T[] },
    categories: InternalCategory[],
    chunkSize: number
): (T & { isNewCategory: boolean })[][] => {
    const chunks: (T & { isNewCategory: boolean })[][] = [];
    let tempArray: (T & { isNewCategory: boolean })[] = [];
    let numLines = 0;

    for (const category of categories) {
        const categorySize = entriesMap[category.id]?.length ?? 0;

        for (let i = 0; i < categorySize; i++) {
            if (numLines === chunkSize || (i === 0 && numLines === chunkSize - 1)) {
                chunks.push(tempArray);
                tempArray = [];
                numLines = 0;
            }
            const item = entriesMap[category.id][i];
            tempArray.push({ ...item, isNewCategory: i === 0 });
            if (i === 0) numLines += 2;
            else numLines++;
        }
    }
    if (tempArray?.length > 0) {
        chunks.push(tempArray);
    }

    return chunks;
};
